/**
 * Pure resume planner for the Ring 1 reattach sweep.
 *
 * After a page reload the client needs to reconnect to every in-flight SSE
 * stream. `planResume` inspects the hydrated forecast-run store state and
 * returns a flat list of runs whose streams must be resumed — one entry per
 * qualifying run.
 *
 * Rules:
 *  - Only RUNNING runs are eligible (PREPROCESSING, COMPLETED, ERRORED,
 *    CANCELLED are all skipped).
 *  - A RUNNING run with no `traceId` is also skipped — there is no server-side
 *    key to resume against.
 *  - `kind` is 'main' for root runs (parentRunId == null) and 'branch' for
 *    everything else.
 *  - `rootRunId` is the run's `conversationId`, which equals the root run's
 *    `runId` for both roots and their branches (all members of a tree share the
 *    root's conversationId by construction — see model.js / reducer.js).
 *
 * This module is intentionally side-effect-free: no React, no network, no
 * mutation of the input state. It may be called from any context (hook,
 * effect, worker, test) without concern for ordering or global state.
 */

import { STATUS } from "./model.js";
import { selectRunsByStatus } from "./selectors.js";

/**
 * Given the forecast-run store state, return the list of runs whose SSE
 * streams need to be resumed after a reload.
 *
 * @param {{ runsById: Record<string, {runId:string, parentRunId:(string|null), conversationId:string, status:string, traceId:(string|null)}> }} state
 * @returns {Array<{ runId: string, traceId: string, kind: 'main'|'branch', rootRunId: string }>}
 */
export function planResume(state) {
  // Collect every RUNNING run then filter to those that have a traceId.
  return selectRunsByStatus(state, STATUS.RUNNING)
    .filter((run) => Boolean(run.traceId))
    .map((run) => ({
      runId: run.runId,
      traceId: run.traceId,
      // parentRunId == null  →  this is a root (main) run.
      kind: run.parentRunId == null ? "main" : "branch",
      // conversationId is the root runId for both roots and branches.
      rootRunId: run.conversationId,
    }));
}

/**
 * Resolve the createdAt a resumed branch run should keep, given the reattach
 * handle reconstructed for it. Prefer an explicit `createdAt`, else the
 * branch's real creation time carried as `submittedAt` (so the re-created run
 * keeps its original chat-line position instead of jumping to "now"), else
 * `undefined` so `makeRun` stamps the current time.
 *
 * @param {{createdAt?: (string|undefined), submittedAt?: (string|undefined)}} handle
 * @returns {string|undefined}
 */
export function pickBranchReattachCreatedAt(handle) {
  return handle?.createdAt ?? handle?.submittedAt ?? undefined;
}

/**
 * Decide whether a reattach that has reached a terminal event may clear the
 * GLOBAL `forecastingPhase` (the lane-0 "Generating Forecast" sweep).
 *
 * A primary (foreground) leg always owns the global phase, so it always clears.
 * A secondary (background) leg normally must NOT touch the focused lane's phase
 * — two legs writing it would flip the focused lane. The ONE exception is the
 * run the user is currently focused on: on reload a single running root resumes
 * passively (secondary), and if the user then re-selects that still-running row
 * `handleSelect` sets the phase to "awaiting" but the foreground reattach
 * early-returns on the exec claim (the background sweep already owns the
 * stream). Nobody would then clear the phase and "Generating Forecast" would
 * stick forever — so the secondary leg completing that focused run clears it.
 *
 * The `reattachRunId != null` guard stops an unresolvable reattach id from
 * matching a null `activeRunId` (null === null) and clearing spuriously.
 *
 * @param {{ isSecondary: boolean, reattachRunOnScreen: boolean }} args — `reattachRunOnScreen`:
 *   whether the run this reattach carries is the one selected at terminal time (the caller's
 *   equality on its own run id against the live selection; false when that id is unresolved).
 * @returns {boolean}
 */
export function shouldClearGlobalPhaseOnTerminal({ isSecondary, reattachRunOnScreen }) {
  return !isSecondary || reattachRunOnScreen === true;
}

/**
 * Resolve the single root the URL says should be FOCUSED on boot — the "URL is
 * the boot focus authority" rule (EPIC #157, Lane E2 / #168). A forecast the
 * user was looking at while it streamed carries its id in the URL's `?forecast=`
 * param (the `installUrlSync` mirror wrote it — the stable `forecastId`, or the
 * client run id before one was allocated). This decides whether that id should
 * elevate a still-running root from the blank-home-boot passive default to a
 * FOREGROUND resume (owning active/phase/chat).
 *
 * The URL id is honored ONLY when it resolves to a run that is itself a still-
 * running root (`runningRootRunIds` — the sweep's planResume "mains"). Returns
 * null otherwise, so blank-home boot (passive resume, nothing focused) is
 * preserved for every case the URL doesn't explicitly claim:
 *  - the URL names no forecast (bare/cross-tab load),
 *  - it names a TERMINAL forecast (completed/errored/cancelled — restoring that
 *    focus is the forecastDeepLink boot effect's job, not the resume sweep's),
 *  - or it names a run that is no longer loaded ("gone" → graceful passive).
 *
 * Pure: no React, no window, no store mutation — the impure URL read + store
 * lookup happen in the caller (useForecastResume) and are passed in.
 *
 * @param {(string|null|undefined)} urlActiveId  the URL `?forecast=` id (forecastId or client runId), or null
 * @param {Record<string, {runId:string, forecastId?:(string|null)}>} runsById  the hydrated run store
 * @param {string[]} runningRootRunIds  runIds of still-running root runs (planResume mains)
 * @returns {string|null}  the runId to focus, or null for passive/blank-home boot
 */
/**
 * Decide whether a log-row click on a RUNNING row (App.jsx's `handleSelect` →
 * `selectForecast`) may open the foreground SSE reattach (Task 2.5/B2,
 * frontend review). A shared row's live updates already arrive over the
 * generic realtime hints channel (Task 2.3a, G13) — a SECOND, dedicated
 * reattach would also claim the global `active`/`forecastingPhase`/chat as if
 * this tab were the row's own foreground generation, which is wrong UI
 * semantics for a viewer who doesn't own the row. Not a security gate: the
 * server's shared read predicate (lifecycle.js's resume gate) already governs
 * what a shared viewer may fetch regardless of what this function returns.
 *
 * `isOwn !== false` (not `isOwn === true`) so a row with no `isOwn` field at
 * all — every owner-leg row before Task 2.5, and any owner-leg row today —
 * keeps reattaching exactly as before; only an explicit `isOwn: false` (a
 * hydrated shared row, per `hydrate.js`'s `rowToRun`) opts out — unless this tab started the
 * update it is running (`sharedRerunHandle`, an in-org collaborator's Update now), which is the
 * viewer's own generation to watch.
 *
 * @param {{id?: string, status?: string, traceId?: (string|null), isOwn?: boolean}} forecast
 * @param {{runId: string, traceId: string}|null} [sharedRerunHandle] `sharedRerunHandleFor(forecast.id)`
 * @returns {boolean}
 */
export function shouldReattachOnSelect(forecast, sharedRerunHandle = null) {
  return (
    forecast?.status === "running" &&
    Boolean(forecast?.traceId) &&
    (forecast?.isOwn !== false || startedHere(forecast, sharedRerunHandle))
  );
}

/** Whether `handle` names the update `forecast` is running: one this tab started. */
function startedHere(forecast, handle) {
  return (
    !!handle &&
    !!forecast?.traceId &&
    handle.runId === forecast.id &&
    handle.traceId === forecast.traceId
  );
}

/**
 * Decide whether App.jsx's `handleSelect` takes the "treat as an in-flight
 * generation you can watch" branch for a RUNNING row (the awaiting animation
 * + a `selectForecast({reattach:true})` attempt), vs. falling through to the
 * plain "just show this row" path used for every terminal row (Task 2.5/B2,
 * frontend review). Withheld for a shared row (`isOwn:false`): its live
 * updates already arrive over the generic realtime hints channel (Task 2.3a,
 * G13), so showing the owner-only "Generating Forecast" animation for a
 * stream this tab will never actually attach to (`shouldReattachOnSelect`
 * requires a traceId this call never satisfies for a shared row that's still
 * running) would leave it stuck forever — nothing would ever fire to clear
 * it, not merely a cosmetic mismatch.
 *
 * Deliberately does NOT require a traceId (unlike `shouldReattachOnSelect`)
 * — a running OWNER row with no traceId yet still takes this branch exactly
 * as it did before this task; only the shared-row case is new. A shared row whose update this
 * tab started takes the branch too: `shouldReattachOnSelect` lets it attach.
 *
 * @param {{id?: string, status?: string, traceId?: (string|null), isOwn?: boolean}} forecast
 * @param {{runId: string, traceId: string}|null} [sharedRerunHandle] `sharedRerunHandleFor(forecast.id)`
 * @returns {boolean}
 */
export function shouldShowRunningRowAsGenerating(forecast, sharedRerunHandle = null) {
  return (
    forecast?.status === "running" &&
    (forecast?.isOwn !== false || startedHere(forecast, sharedRerunHandle))
  );
}

export function resolveSingleRootFocus(urlActiveId, runsById, runningRootRunIds) {
  if (!urlActiveId) return null;
  // Fast path: the URL id is usually the client runId, which is a key in
  // runsById — an O(1) lookup, no array allocation. Only fall back to scanning
  // values for the stable-forecastId id-space (a forecastId is never a key).
  // Equivalent to matching `r.runId === urlActiveId || r.forecastId === urlActiveId`
  // for real inputs (runId/forecastId are disjoint UUID spaces).
  const runId = runsById[urlActiveId]
    ? urlActiveId
    : (Object.values(runsById).find((r) => r.forecastId === urlActiveId)?.runId ?? null);
  // A `?forecast=` link may name a BRANCH (SOO-862): the deep link opens its root, so the
  // sweep focuses that root when it is the one still running.
  const rootId = runId ? treeRootOf(runsById, runId) : null;
  return rootId && runningRootRunIds.includes(rootId) ? rootId : null;
}

/**
 * The root a run belongs to in `runsById`, followed through `parentRunId` rather than read off
 * the stored `conversationId`: hydrating the shared list page by page leaves a branch whose
 * parent arrived on another page with `conversationId` set to itself. Stops at a parent that is
 * not loaded (returning that parent's id) or at a cycle.
 *
 * @param {Record<string, {parentRunId?: (string|null)}>|null|undefined} runsById
 * @param {string} runId
 * @returns {string}
 */
export function treeRootOf(runsById, runId) {
  const seen = new Set();
  let id = runId;
  while (!seen.has(id)) {
    seen.add(id);
    const r = runsById?.[id];
    if (!r || r.parentRunId == null) return id;
    id = r.parentRunId;
  }
  return id;
}
